import { Component, ErrorInfo, ReactNode } from "react";

/**
 * Stops one broken section from taking a whole page down with it.
 *
 * This exists because it already happened. A dashboard section on /home read a property
 * off undefined state, React unmounted the tree, and every organisation admin got a
 * white screen — not just a missing dashboard, but no home page at all: no activity log,
 * no charts, nothing. The section was the least important thing on the page and it was
 * the only thing that could not be lost.
 *
 * A boundary cannot be a hook, so this is a class component; that is React's API, not a
 * style choice.
 *
 * Deliberately quiet. The fallback is a line of grey text, not the red `ErrorCard` the
 * page uses when the page itself has failed, because a section that cannot load is not
 * the same event as the page being broken and should not shout like one.
 */
interface Props {
  children: ReactNode;
  /** Shown instead of the section. Keep it plain. */
  message?: string;
}

interface State {
  hasError: boolean;
}

class SectionBoundary extends Component<Props, State> {
  public state: State = { hasError: false };

  public static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, info: ErrorInfo) {
    // Logged rather than swallowed: a section that quietly disappears in production is
    // worse than one that fails loudly, because nobody finds out it is gone.
    // eslint-disable-next-line no-console
    console.error("[section] render failed:", error, info.componentStack);
  }

  public render() {
    if (this.state.hasError) {
      return (
        <div className="rounded-3xl bg-white p-4">
          <div className="text-sm text-gray-500">
            {this.props.message ?? "ยังแสดงส่วนนี้ไม่ได้ในขณะนี้"}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default SectionBoundary;
